import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { queryKeys } from 'api/config';
import { produce } from 'immer';
import { logError } from 'lib/sentry/logError';
import { useQueryHTTPRequest } from 'shared_DEPRECATED/hooks';
import { Nullable } from 'shared_DEPRECATED/types';

import {
  Message,
  MESSAGE_STATUS,
  Messages,
} from 'features/chat/config/types/Message';

const CHAT_MESSAGES_COUNT_TO_LOAD = 20;

export const useChatMessagesQuery = ({
  chatId,
  isSocketReady,
}: {
  chatId: Nullable<string>;
  isSocketReady?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { request } = useQueryHTTPRequest();

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteQuery<{
      cursor: string;
      messages: Messages;
      unreadMessagesCount: number;
    }>({
      queryKey: [queryKeys.chatMessages, chatId],
      queryFn: ({ pageParam }) => {
        const data = queryClient.getQueryData<
          { unreadMessagesNumber: number } | undefined
        >([queryKeys.chats, chatId]);
        const url = `/api/chats/${chatId}/history`;
        const urlSearchParams = new URLSearchParams();

        if (pageParam) {
          urlSearchParams.append(
            'limit',
            pageParam.limit ?? CHAT_MESSAGES_COUNT_TO_LOAD
          );
          urlSearchParams.append('from_message_id', pageParam.fromMessageId);
          urlSearchParams.append('direction', pageParam.direction);
        } else {
          urlSearchParams.append(
            'limit',
            `${Math.max(
              data?.unreadMessagesNumber || 0,
              CHAT_MESSAGES_COUNT_TO_LOAD
            )}`
          );
          urlSearchParams.append('direction', 'down');
        }

        return request({ url: `${url}?${urlSearchParams.toString()}` });
      },
      getNextPageParam: (lastPage) =>
        lastPage?.messages.length
          ? {
              fromMessageId: lastPage?.messages.at(-1)?.messageId,
              direction: 'up',
            }
          : undefined,
      select: (data) => ({
        pages: data.pages.map((page) => ({
          ...page,
          messages: [...page.messages.slice().reverse()],
        })),
        pageParams: [...data.pageParams],
      }),
      enabled: !!chatId && isSocketReady,
      useErrorBoundary: false,
      onError: (err) => {
        logError(err);
      },
    });

  const messages = useMemo(
    () =>
      data?.pages.reduce<Messages>((messagesAcc, currentPage) => {
        return [...currentPage.messages, ...messagesAcc];
      }, []),
    [data]
  );

  const hasFailedMessages =
    messages?.some(({ status }) => status === MESSAGE_STATUS.FAILED) ?? false;

  const setNewMessages = useCallback(
    (onMessageSet: (messages: Messages) => Messages) => {
      queryClient.setQueryData(
        [queryKeys.chatMessages, chatId],
        (oldData?: {
          pages: {
            messages: Messages;
            cursor: string;
            unreadMessagesCount: number;
          }[];
          pageParams: (string | null)[];
        }) => {
          if (!oldData) {
            oldData = {
              pages: [
                {
                  messages: [],
                  cursor: '',
                  unreadMessagesCount: 0,
                },
              ],
              pageParams: [],
            };
          }

          const newPages = oldData.pages.map((page) => ({
            ...page,
            messages: onMessageSet(page.messages),
          }));

          return {
            ...oldData,
            pages: newPages,
            pageParams: [...oldData.pageParams],
          };
        }
      );
    },
    [chatId, queryClient]
  );

  // adds new message to the first page only
  const addNewMessage = useCallback(
    (newMessage: Message) => {
      queryClient.setQueryData(
        [queryKeys.chatMessages, chatId],
        (oldData?: {
          pages: {
            messages: Messages;
            cursor: string;
            unreadMessagesCount: number;
          }[];
          pageParams: (string | null)[];
        }) => {
          if (!oldData) {
            oldData = {
              pages: [
                {
                  messages: [],
                  cursor: '',
                  unreadMessagesCount: 0,
                },
              ],
              pageParams: [],
            };
          }

          const newPages = produce(oldData.pages, (draft) => {
            draft[0].messages.unshift(newMessage);
          });

          return {
            ...oldData,
            pages: newPages,
            pageParams: [...oldData.pageParams],
          };
        }
      );
    },
    [chatId, queryClient]
  );

  return {
    fetchNextPage,
    hasNextPage,
    isFetching,
    hasFailedMessages,
    messages,
    setNewMessages,
    addNewMessage,
    isLoading,
  };
};
